import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

export const CTA = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.5 });
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]);

  return (
    <section
      ref={containerRef}
      className="py-24 relative overflow-hidden"
      style={{ backgroundColor: '#f7f5f2' }}
    >
      {/* Animated background elements */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
          style={{ backgroundColor: 'rgba(97, 82, 90, 0.1)' }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl"
          style={{ backgroundColor: 'rgba(40, 55, 80, 0.1)' }}
        />
      </motion.div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{
            opacity: isInView ? 1 : 0,
            scale: isInView ? 1 : 0.9,
          }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.3, 1] }}
          className="text-center p-12 md:p-16 rounded-3xl relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #283750 0%, #000 100%)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Animated sparkles */}
          <motion.div
            animate={{
              y: [-10, 10, -10],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute top-8 right-8"
          >
            <Sparkles size={32} style={{ color: '#fad24b' }} />
          </motion.div>
          <motion.div
            animate={{
              y: [10, -10, 10],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2,
            }}
            className="absolute bottom-8 left-8"
          >
            <Sparkles size={32} style={{ color: '#3dd3ee' }} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: isInView ? 1 : 0,
              y: isInView ? 0 : 20,
            }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4 inline-flex items-center space-x-2 px-4 py-2 rounded-full"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <span className="text-white font-medium">Limited Time Offer</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: isInView ? 1 : 0,
              y: isInView ? 0 : 20,
            }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-4xl md:text-5xl font-bold mb-6 text-white"
          >
            Ready to Transform Your
            <br />
            Event Management?
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: isInView ? 1 : 0,
              y: isInView ? 0 : 20,
            }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl mb-10"
            style={{ color: '#bbb5ae' }}
          >
            Start your 14-day free trial today. No credit card required.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: isInView ? 1 : 0,
              y: isInView ? 0 : 20,
            }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              size="lg"
              className="text-lg px-8 py-6 font-medium transition-all duration-300 hover:scale-105 group"
              style={{ backgroundColor: 'white', color: '#283750' }}
            >
              Start Free Trial
              <ArrowRight
                className="ml-2 group-hover:translate-x-1 transition-transform"
                size={20}
              />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 font-medium transition-all duration-300 hover:scale-105"
              style={{
                borderColor: 'white',
                color: 'white',
                backgroundColor: 'transparent',
              }}
            >
              Schedule Demo
            </Button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: isInView ? 1 : 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-8 text-sm"
            style={{ color: '#bbb5ae' }}
          >
            Join 10,000+ event professionals already using EventMaster
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};
